import { z } from "zod";
import { optionalEnum } from "./_helpers/optional";

const fieldTypes = [
  "text",
  "number",
  "boolean",
  "date",
  "datetime",
  "select",
  "multiselect",
  "reference",
] as const;

const uiWidgets = [
  "text",
  "textarea",
  "number",
  "currency",
  "checkbox",
  "toggle",
  "date",
  "datetime",
  "select",
  "radio",
  "checkbox_group",
  "multiselect",
  "url",
  "phone",
  "email",
] as const;

const SelectOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  order: z.number().optional(),
});

export const CustomFieldDefinitionSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  entity_type: z.string().describe("Entity Type"),
  field_key: z.string().describe("Field Key"),
  label: z.string().describe("Label"),
  description: z.string().nullable().describe("Description"),
  field_type: z.enum(fieldTypes).describe("Field Type"),
  ui_widget: z.enum(uiWidgets).nullable().describe("UI Widget"),
  options: z.array(SelectOptionSchema).nullable().describe("Options"),
  validation: z.record(z.unknown()).nullable().describe("Validation"),
  default_value: z.unknown().nullable().describe("Default Value"),
  is_required: z.boolean().describe("Required"),
  is_system: z.boolean().describe("System"),
  is_active: z.boolean().describe("Active"),
  is_filterable: z.boolean().describe("Filterable"),
  is_reportable: z.boolean().describe("Reportable"),
  sort_order: z.number().describe("Sort Order"),
  group_name: z.string().nullable().describe("Group Name"),
  jsonb_column: z.string().describe("JSONB Column"),
  source_pack_id: z.string().uuid().nullable().describe("Source Pack"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type CustomFieldDefinition = z.infer<typeof CustomFieldDefinitionSchema>;

export const CustomFieldDefinitionInsertSchema = z.object({
  entity_type: z.string().describe("Entity Type"),
  field_key: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/, "Must be lowercase snake_case")
    .describe("Field Key"),
  label: z.string().describe("Label"),
  description: z.string().optional().describe("Description"),
  field_type: z.enum(fieldTypes).describe("Field Type"),
  ui_widget: optionalEnum(uiWidgets).describe("UI Widget"),
  options: z.array(SelectOptionSchema).optional().describe("Options"),
  validation: z.record(z.unknown()).optional().describe("Validation"),
  default_value: z.unknown().optional().describe("Default Value"),
  is_required: z.boolean().optional().describe("Required"),
  is_filterable: z.boolean().optional().describe("Filterable"),
  is_reportable: z.boolean().optional().describe("Reportable"),
  sort_order: z.number().optional().describe("Sort Order"),
  group_name: z.string().optional().describe("Group Name"),
  jsonb_column: z.string().optional().describe("JSONB Column"),
});

export const CustomFieldDefinitionUpdateSchema = z.object({
  label: z.string().optional().describe("Label"),
  description: z.string().optional().describe("Description"),
  ui_widget: optionalEnum(uiWidgets).describe("UI Widget"),
  options: z.array(SelectOptionSchema).optional().describe("Options"),
  validation: z.record(z.unknown()).optional().describe("Validation"),
  default_value: z.unknown().optional().describe("Default Value"),
  is_required: z.boolean().optional().describe("Required"),
  is_active: z.boolean().optional().describe("Active"),
  is_filterable: z.boolean().optional().describe("Filterable"),
  is_reportable: z.boolean().optional().describe("Reportable"),
  sort_order: z.number().optional().describe("Sort Order"),
  group_name: z.string().optional().describe("Group Name"),
});

export const CustomFieldDefinitionQuerySchema = z.object({
  entity_type: z.string().optional(),
  group_name: z.string().optional(),
  is_active: z.coerce.boolean().optional(),
});

const GroupMetadataSchema = z.object({
  label: z.string().describe("Label"),
  description: z.string().optional().describe("Description"),
  sort_order: z.number().optional().describe("Sort Order"),
  icon: z.string().optional().describe("Icon"),
});

export const IndustrySolutionPackSchema = z.object({
  id: z.string().uuid().describe("ID"),
  slug: z.string().describe("Slug"),
  name: z.string().describe("Name"),
  description: z.string().nullable().describe("Description"),
  icon: z.string().nullable().describe("Icon"),
  version: z.string().describe("Version"),
  is_active: z.boolean().describe("Active"),
  field_templates: z.array(z.record(z.unknown())).describe("Field Templates"),
  commission_template: z
    .record(z.unknown())
    .nullable()
    .describe("Commission Template"),
  product_templates: z
    .array(z.record(z.unknown()))
    .nullable()
    .describe("Product Templates"),
  // ADR-0022 merchandising + maintainer metadata — nullable + optional so
  // existing DB rows that predate these columns (which return null) parse OK.
  long_description: z
    .string()
    .nullable()
    .optional()
    .describe("Long Description"),
  best_for: z.array(z.string()).nullable().optional().describe("Best For"),
  not_for: z.array(z.string()).nullable().optional().describe("Not For"),
  entity_summaries: z
    .record(z.string(), z.string())
    .nullable()
    .optional()
    .describe("Per-Entity Summaries"),
  group_metadata: z
    .record(z.string(), GroupMetadataSchema)
    .nullable()
    .optional()
    .describe("Per-Group Metadata"),
  maintainer: z.string().nullable().optional().describe("Maintainer"),
  equipment_types: z
    .array(z.record(z.unknown()))
    .nullable()
    .optional()
    .describe("Equipment Types"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type IndustrySolutionPack = z.infer<typeof IndustrySolutionPackSchema>;
